import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRegisterSW } from "virtual:pwa-register/react";
import { shouldAutoApplyUpdate } from "../util/pwaUpdatePolicy";
import { retryPendingChunkRecovery } from "../util/chunkLoadRecovery";
import { startPeriodicUpdateCheck } from "../util/swPeriodicUpdate";

// #295, revised #1920 — surfaces the vite-plugin-pwa "new version available"
// state. registerType is "prompt", so an updated service worker installs but
// waits; updateServiceWorker(true) activates it and reloads the page.
//
// #1920: a Dismiss button meant a scribe who dismissed (or never saw) this
// snackbar kept running an old, precached shell indefinitely — one whose
// bundle hashes a later deploy had already removed from the CDN, surfacing
// as "Failed to fetch" (bug #183). There is no Dismiss any more: the moment
// an update is waiting, it is applied automatically on a route the explicit
// safe-list in `pwaUpdatePolicy.js` certifies (`shouldAutoApplyUpdate`,
// kiosk mid-ride/mid-cell-entry safety); everywhere else (the scoring sheet,
// the class grid, the OIDC callback) it waits silently (#3437: no on-screen
// notice, the scribe cannot act on it) and the effect re-fires on every route change so the update applies the
// moment the scribe reaches a safe route. Applying it there is no different
// from the full page reload sign-out already performs.
//
// #2170: this is also the retry point for a chunk-load recovery
// (chunkLoadRecovery.js) deferred because it arrived on an unsafe route —
// same "re-evaluate on every route change" mechanism, reused rather than
// duplicated.
//
// #3447: this is also where a new build is DETECTED. The browser only looks
// for a new sw.js on a full navigation, which a kiosk SPA never does, so once
// the SW registers, swPeriodicUpdate.js re-checks on a timer and on
// visibilitychange. A waiting worker it finds flows through the same
// needRefresh + shouldAutoApplyUpdate path above, so it never reloads a scribe
// mid-ride. `updateCheckIntervalMs` is the engine option (default 5 minutes).
export default function ReloadPrompt({ updateCheckIntervalMs }) {
  const location = useLocation();
  const [registration, setRegistration] = useState(null);
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, reg) {
      if (reg) setRegistration(reg);
    },
  });

  useEffect(() => {
    if (!registration) return undefined;
    return startPeriodicUpdateCheck(registration, { intervalMs: updateCheckIntervalMs });
  }, [registration, updateCheckIntervalMs]);

  useEffect(() => {
    if (needRefresh && shouldAutoApplyUpdate(location.pathname)) {
      updateServiceWorker(true);
    }
    retryPendingChunkRecovery(location.pathname);
  }, [needRefresh, location.pathname, updateServiceWorker]);

  // #3437 -- renders nothing. The "a new version is ready" snackbar that used
  // to show here on the scoring sheet / class grid is gone: the scribe has
  // nothing to do about it (the update applies itself the moment she reaches
  // a safe route, and "Check for updates" in the account menu is her own
  // manual door). Only the auto-apply above remains.
  return null;
}
